import { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";
import { useNotification } from "./NotifPopup";

const REQ_GROUP_LIST = "page33-laporan-khusus-list";
const REQ_GROUP_CHAIN = "page33-laporan-khusus-chain";

const PANEL_BOX_STYLE = {
  background: "linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%)",
  border: "1px solid #d8e0ee",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
};

const PANEL_BUTTON_STYLE = {
  background: "#0f2d5c",
  color: "#ffffff",
  border: "1px solid #0f2d5c",
  boxShadow: "0 4px 12px rgba(15, 45, 92, 0.28)",
};

const jakartaDateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const getJakartaDate = () => {
  const parts = jakartaDateFormatter.formatToParts(new Date());
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
};

const getDefaultStartDate = () => `${getJakartaDate().slice(0, 4)}-01-01`;

const formatDate = (value) => {
  const text = String(value || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return text || "-";
  const [year, month, day] = text.split("-");
  return `${day}-${month}-${year}`;
};

const statusLabel = (value) => (
  String(value || "").trim().toLowerCase() === "done" ? "Selesai" : "Belum Selesai"
);

const buildPelapor = (row) => {
  const cabang = String(row?.nama_cabang || row?.kode_cabang || "-").trim() || "-";
  const user = String(row?.user || "-").trim() || "-";
  return `${cabang} (${user})`;
};

const getLampiranUrl = (item) => {
  const path = String(item?.path || "").trim();
  if (path) return path.startsWith("http") ? path : `${backendPort}${path}`;
  const file = String(item?.file || "").trim();
  return file ? `${backendPort}/uploads/dokumen/${file}` : "";
};

const REPORT_COLUMNS = [
  { key: "nomor", label: "NO. LAPORAN", minWidth: 175 },
  { key: "tanggal", label: "TANGGAL", minWidth: 105 },
  { key: "pelapor", label: "PELAPOR", minWidth: 220 },
  { key: "jenis", label: "JENIS", minWidth: 120 },
  { key: "identitas", label: "NO. POL/SERI BAN", minWidth: 150 },
  { key: "topik", label: "TOPIK", minWidth: 180 },
  { key: "status", label: "STATUS", minWidth: 120 },
  { key: "uraian", label: "URAIAN", minWidth: 280 },
];

const getReportValue = (row, key) => {
  if (key === "pelapor") return buildPelapor(row);
  if (key === "jenis") return String(row?.jenis_laporan || "-");
  if (key === "identitas") return String(row?.no_pol_seri || row?.identity || "-");
  if (key === "status") return statusLabel(row?.status_topik);
  if (key === "uraian") return String(row?.uraian || "-");
  if (key === "tanggal") return String(row?.tanggal || "");
  return String(row?.[key] || "-");
};

const escapeExcelHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const sanitizeFilename = (value) => String(value || "laporan")
  .trim()
  .replace(/[\\/:*?"<>|]+/g, "_")
  .replace(/\s+/g, "_");

const downloadExcelRows = ({ headers = [], bodyRows = [], filename }) => {
  const headHtml = headers
    .map((header) => (
      `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeExcelHtml(header)}</th>`
    ))
    .join("");
  const bodyHtml = bodyRows
    .map((cells) => (
      `<tr>${cells.map((cell) => `<td>${escapeExcelHtml(cell)}</td>`).join("")}</tr>`
    ))
    .join("");
  const html = `\uFEFF<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${sanitizeFilename(filename)}.xls`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const getLampiranSummary = (row) => {
  const lampiran = Array.isArray(row?.lampiran) ? row.lampiran : [];
  if (!lampiran.length) return "-";
  return lampiran
    .map((item, index) => String(
      item?.caption || item?.name || item?.file || `Lampiran ${index + 1}`
    ).trim())
    .filter(Boolean)
    .join(" | ");
};

export function LaporanKhususRow({
  row,
  open,
  onToggle,
}) {
  const { showNotif } = useNotification();
  const [selectedStatus, setSelectedStatus] = useState("notdone");
  const [tanggalAwal, setTanggalAwal] = useState(getDefaultStartDate);
  const [tanggalAkhir, setTanggalAkhir] = useState(getJakartaDate);
  const [rows, setRows] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [includeText, setIncludeText] = useState("");
  const [excludeText, setExcludeText] = useState("");
  const [pageSize, setPageSize] = useState("25");
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("tanggal");
  const [sortDir, setSortDir] = useState("desc");
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainLoading, setChainLoading] = useState(false);
  const [chainRows, setChainRows] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const listRequestSeq = useRef(0);
  const chainRequestSeq = useRef(0);

  useEffect(() => () => {
    listRequestSeq.current += 1;
    chainRequestSeq.current += 1;
    api.cancelPendingRequests({ group: REQ_GROUP_LIST, reason: "Keluar dari Laporan Khusus" });
    api.cancelPendingRequests({ group: REQ_GROUP_CHAIN, reason: "Keluar dari Laporan Khusus" });
  }, []);

  const filteredRows = useMemo(() => {
    const include = String(includeText || "").trim().toLowerCase();
    const exclude = String(excludeText || "").trim().toLowerCase();
    const filtered = rows.filter((item) => {
      const text = REPORT_COLUMNS
        .map((column) => getReportValue(item, column.key))
        .join(" ")
        .toLowerCase();
      if (include && !text.includes(include)) return false;
      if (exclude && text.includes(exclude)) return false;
      return true;
    });
    return [...filtered].sort((left, right) => {
      const leftValue = getReportValue(left, sortBy);
      const rightValue = getReportValue(right, sortBy);
      const compared = String(leftValue).localeCompare(String(rightValue), "id", {
        numeric: true,
        sensitivity: "base",
      });
      return sortDir === "asc" ? compared : -compared;
    });
  }, [excludeText, includeText, rows, sortBy, sortDir]);

  const effectivePageSize = pageSize === "all"
    ? Math.max(filteredRows.length, 1)
    : Math.max(Number(pageSize || 25), 1);
  const totalPages = Math.max(Math.ceil(filteredRows.length / effectivePageSize), 1);
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * effectivePageSize;
  const pagedRows = filteredRows.slice(startIndex, startIndex + effectivePageSize);

  useEffect(() => {
    setPage(1);
  }, [excludeText, includeText, pageSize, rows]);

  const loadRows = async () => {
    if (!tanggalAwal || !tanggalAkhir) {
      showNotif("error", "Tanggal Awal dan Tanggal Akhir wajib diisi.");
      return;
    }
    if (tanggalAwal > tanggalAkhir) {
      showNotif("error", "Tanggal Awal tidak boleh melebihi Tanggal Akhir.");
      return;
    }

    const requestSeq = listRequestSeq.current + 1;
    listRequestSeq.current = requestSeq;
    try {
      api.cancelPendingRequests({ group: REQ_GROUP_LIST, reason: "Filter Laporan Khusus terbaru" });
      setLoading(true);
      const { data } = await api.get(`${backendPort}/api/laporan-khusus`, {
        params: {
          status: selectedStatus,
          tanggal_awal: tanggalAwal,
          tanggal_akhir: tanggalAkhir,
        },
        requestGroup: REQ_GROUP_LIST,
        timeout: 120000,
      });
      if (requestSeq !== listRequestSeq.current) return;
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setHasLoaded(true);
      setShowResultsModal(true);
    } catch (err) {
      if (requestSeq !== listRequestSeq.current) return;
      if (api.isCanceledError(err)) return;
      console.error("Gagal memuat Laporan Khusus:", err);
      setRows([]);
      setHasLoaded(true);
      showNotif("error", err?.response?.data?.message || "Gagal memuat Laporan Khusus.");
    } finally {
      if (requestSeq === listRequestSeq.current) setLoading(false);
    }
  };

  const openChain = async (report) => {
    const id = Number(report?.id || 0);
    if (!Number.isInteger(id) || id <= 0) return;
    setSelectedReport(report);
    setChainRows([]);
    setShowChainModal(true);
    const requestSeq = chainRequestSeq.current + 1;
    chainRequestSeq.current = requestSeq;
    try {
      api.cancelPendingRequests({ group: REQ_GROUP_CHAIN, reason: "Rantai Laporan Khusus terbaru" });
      setChainLoading(true);
      const { data } = await api.get(`${backendPort}/api/laporan-khusus/${id}/rantai`, {
        requestGroup: REQ_GROUP_CHAIN,
        timeout: 120000,
      });
      if (requestSeq !== chainRequestSeq.current) return;
      setChainRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      if (requestSeq !== chainRequestSeq.current) return;
      if (api.isCanceledError(err)) return;
      console.error("Gagal memuat rantai Laporan Khusus:", err);
      showNotif("error", err?.response?.data?.message || "Gagal memuat rantai Laporan Khusus.");
    } finally {
      if (requestSeq === chainRequestSeq.current) setChainLoading(false);
    }
  };

  const closeChainModal = () => {
    chainRequestSeq.current += 1;
    api.cancelPendingRequests({ group: REQ_GROUP_CHAIN, reason: "Modal rantai ditutup" });
    setShowChainModal(false);
    setSelectedReport(null);
    setChainRows([]);
  };

  const clearLoadedRows = () => {
    setRows([]);
    setHasLoaded(false);
    setShowResultsModal(false);
  };

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(columnKey);
    setSortDir("asc");
  };

  const downloadReportRows = (exportRows, suffix) => {
    downloadExcelRows({
      headers: ["NO.", ...REPORT_COLUMNS.map((column) => column.label)],
      bodyRows: (Array.isArray(exportRows) ? exportRows : []).map((item, index) => [
        index + 1,
        ...REPORT_COLUMNS.map((column) => {
          const value = getReportValue(item, column.key);
          return column.key === "tanggal" ? formatDate(value) : value;
        }),
      ]),
      filename: `LAPORAN_KHUSUS_${tanggalAwal}_s.d_${tanggalAkhir}_${suffix}`,
    });
  };

  const downloadChainRows = () => {
    downloadExcelRows({
      headers: [
        "NO.",
        "NO. LAPORAN",
        "TANGGAL",
        "PELAPOR",
        "JENIS",
        "NO. POL/SERI BAN",
        "TOPIK",
        "STATUS",
        "URAIAN",
        "LAMPIRAN",
      ],
      bodyRows: (Array.isArray(chainRows) ? chainRows : []).map((item, index) => [
        index + 1,
        String(item?.nomor || "-"),
        formatDate(item?.tanggal),
        buildPelapor(item),
        String(item?.jenis_laporan || "-"),
        String(item?.no_pol_seri || item?.identity || "-"),
        String(item?.topik || "-"),
        statusLabel(item?.status_topik),
        String(item?.uraian || "-"),
        getLampiranSummary(item),
      ]),
      filename: `RANTAI_LAPORAN_KHUSUS_${String(selectedReport?.nomor || "unknown")}`,
    });
  };

  return (
    <>
      <div
        className="px-3 py-2 rounded"
        style={{ ...PANEL_BOX_STYLE, cursor: open ? "default" : "pointer" }}
        onClick={() => {
          if (!open) onToggle();
        }}
      >
        <div className="row col-md-12 align-items-center g-2">
          <div
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
            style={{ ...PANEL_BUTTON_STYLE, cursor: "pointer" }}
            className="multiBaris col-md-2 px-2 py-2 rounded text-xs font-medium"
          >
            {row?.label || "LAPORAN KHUSUS"}
          </div>
          <div className="col-md-8 text-gray-600">{row?.info || ""}</div>
          <div className="col-md-2 d-flex justify-content-md-end">
            {hasLoaded ? (
              <span className="px-2 py-1 rounded text-xs font-medium" style={{ background: "#fed7aa", color: "#7f1d1d" }}>
                {`${rows.length} baris`}
              </span>
            ) : (
              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                {row?.badge || "No Data"}
              </span>
            )}
          </div>
        </div>

        {open && (
          <div className="mt-3" onClick={(event) => event.stopPropagation()}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label mb-1">Status</label>
                <select
                  className="form-select form-select-sm"
                  value={selectedStatus}
                  disabled={loading}
                  onChange={(event) => {
                    setSelectedStatus(event.target.value);
                    clearLoadedRows();
                  }}
                >
                  <option value="notdone">Perlu Tindak Lanjut</option>
                  <option value="all">Semua Laporan</option>
                </select>
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label mb-1">Tanggal Awal</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={tanggalAwal}
                  disabled={loading}
                  onChange={(event) => {
                    setTanggalAwal(event.target.value);
                    clearLoadedRows();
                  }}
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="form-label mb-1">Tanggal Akhir</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={tanggalAkhir}
                  disabled={loading}
                  onChange={(event) => {
                    setTanggalAkhir(event.target.value);
                    clearLoadedRows();
                  }}
                />
              </div>
              <div className="col-12 col-md-3 d-flex justify-content-md-end">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={loading}
                  onClick={loadRows}
                >
                  {loading ? "Memuat..." : "Tampilkan"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showResultsModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ zIndex: 1300 }}>
            <div
              className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
              style={{ width: "min(1600px, 98vw)", maxWidth: "98vw" }}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">LAPORAN KHUSUS</h5>
                    <div className="text-muted small">
                      {`Tanggal Laporan: ${formatDate(tanggalAwal)} s/d ${formatDate(tanggalAkhir)}`}
                    </div>
                  </div>
                  <button type="button" className="btn-close" onClick={() => setShowResultsModal(false)} />
                </div>
                <div className="modal-body p-3">
                  <section className="bg-white rounded-lg p-3 border">
                    <div className="d-flex justify-content-end gap-2 mb-3 flex-wrap">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => downloadReportRows(filteredRows, "preview")}
                      >
                        Download Preview
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => downloadReportRows(rows, "all")}
                      >
                        Download All
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowResultsModal(false)}
                      >
                        Close
                      </button>
                    </div>

                    <div className="row g-2 mb-3">
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">Filter Mengandung Teks</label>
                        <input
                          className="form-control form-control-sm"
                          value={includeText}
                          onChange={(event) => setIncludeText(event.target.value)}
                          placeholder="baris mengandung teks.."
                        />
                      </div>
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">Filter Negasi Teks</label>
                        <input
                          className="form-control form-control-sm"
                          value={excludeText}
                          onChange={(event) => setExcludeText(event.target.value)}
                          placeholder="baris tidak mengandung teks.."
                        />
                      </div>
                      <div className="col-12 col-md-3" />
                      <div className="col-12 col-md-3">
                        <label className="form-label mb-1">
                          {`Ditampilkan ${pagedRows.length} | Total: ${filteredRows.length}`}
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={pageSize}
                          onChange={(event) => setPageSize(event.target.value)}
                        >
                          {[10, 25, 50, 100].map((size) => (
                            <option key={size} value={String(size)}>{size}</option>
                          ))}
                          <option value="all">Semua</option>
                        </select>
                      </div>
                    </div>

                    <div
                      className="border rounded report-scroll-hover-x"
                      style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}
                    >
                      <table className="table table-sm mb-0 report34-table">
                        <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                          <tr>
                            <th
                              style={{
                                minWidth: 50,
                                background: "#0f2d5c",
                                color: "#ffffff",
                                fontWeight: 400,
                              }}
                            >
                              NO.
                            </th>
                            {REPORT_COLUMNS.map((column) => (
                              <th
                                key={column.key}
                                style={{
                                  minWidth: column.minWidth,
                                  background: "#0f2d5c",
                                  color: "#ffffff",
                                  fontWeight: 400,
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-decoration-none"
                                  style={{
                                    color: "#ffffff",
                                    fontWeight: 400,
                                    width: "100%",
                                    textAlign: "left",
                                  }}
                                  onClick={() => handleSort(column.key)}
                                >
                                  {`${column.label}${sortBy === column.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}`}
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRows.length === 0 && (
                            <tr>
                              <td colSpan={REPORT_COLUMNS.length + 1} className="text-center text-muted py-3">
                                Tidak ada data.
                              </td>
                            </tr>
                          )}
                          {pagedRows.map((item, index) => (
                            <tr key={`laporan-khusus-modal-${item.id}`}>
                              <td style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}>
                                {startIndex + index + 1}
                              </td>
                              {REPORT_COLUMNS.map((column) => {
                                const value = getReportValue(item, column.key);
                                return (
                                  <td
                                    key={`${item.id}-${column.key}`}
                                    style={{
                                      paddingTop: "0.25rem",
                                      paddingBottom: "0.25rem",
                                      whiteSpace: column.key === "uraian" ? "normal" : "nowrap",
                                    }}
                                  >
                                    {column.key === "nomor" ? (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 text-start"
                                        onClick={() => openChain(item)}
                                      >
                                        {value}
                                      </button>
                                    ) : column.key === "tanggal" ? (
                                      formatDate(value)
                                    ) : (
                                      value
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="d-flex justify-content-between align-items-center gap-2 mt-3">
                      <div className="small text-muted">{`Halaman ${safePage} dari ${totalPages}`}</div>
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={safePage <= 1}
                          onClick={() => setPage((current) => Math.max(current - 1, 1))}
                        >
                          Sebelumnya
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={safePage >= totalPages}
                          onClick={() => setPage((current) => Math.min(current + 1, totalPages))}
                        >
                          Berikutnya
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1290 }} />
        </>
      )}

      {showChainModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ zIndex: 1400 }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <div>
                    <h5 className="modal-title">Rantai Laporan Khusus</h5>
                    <div className="small text-muted">{String(selectedReport?.nomor || "-")}</div>
                  </div>
                  <button type="button" className="btn-close" onClick={closeChainModal} />
                </div>
                <div className="modal-body">
                  <div className="mb-2 d-flex justify-content-between align-items-start gap-2">
                    <span style={{ fontWeight: 700, color: "#7a1f1f" }}>
                      {`DATA RANTAI LAPORAN KHUSUS ${String(selectedReport?.nomor || "-")}`}
                    </span>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm align-self-start"
                        disabled={chainLoading}
                        onClick={downloadChainRows}
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm align-self-start"
                        onClick={closeChainModal}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  {chainLoading && (
                    <div className="text-center text-muted py-4">Memuat rantai laporan...</div>
                  )}
                  {!chainLoading && chainRows.length === 0 && (
                    <div className="text-center text-muted py-4">Rantai laporan tidak ditemukan.</div>
                  )}
                  {!chainLoading && chainRows.map((item, index) => {
                    const lampiran = Array.isArray(item?.lampiran) ? item.lampiran : [];
                    return (
                      <div className="border rounded p-3 mb-3" key={`chain-${item.id}`}>
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div className="fw-semibold">{`Tahap ${index + 1}: ${String(item?.nomor || "-")}`}</div>
                          <span className={`badge ${String(item?.status_topik || "").toLowerCase() === "done" ? "bg-success" : "bg-warning text-dark"}`}>
                            {statusLabel(item?.status_topik)}
                          </span>
                        </div>
                        <div className="row g-2 mb-2">
                          <div className="col-12 col-md-3">
                            <div className="small text-muted">Tanggal</div>
                            <div>{formatDate(item?.tanggal)}</div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="small text-muted">Pelapor</div>
                            <div>{buildPelapor(item)}</div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="small text-muted">Jenis / Identitas</div>
                            <div>{`${String(item?.jenis_laporan || "-")} / ${String(item?.no_pol_seri || item?.identity || "-")}`}</div>
                          </div>
                          <div className="col-12 col-md-3">
                            <div className="small text-muted">Topik</div>
                            <div>{String(item?.topik || "-")}</div>
                          </div>
                        </div>
                        <div className="mb-2">
                          <div className="small text-muted">Uraian</div>
                          <div style={{ whiteSpace: "pre-wrap" }}>{String(item?.uraian || "-")}</div>
                        </div>
                        <div>
                          <div className="small text-muted mb-1">{`Lampiran (${lampiran.length})`}</div>
                          {lampiran.length === 0 ? (
                            <div className="text-muted small">Tidak ada lampiran.</div>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {lampiran.map((file, fileIndex) => {
                                const url = getLampiranUrl(file);
                                const label = String(file?.caption || file?.name || file?.file || `Lampiran ${fileIndex + 1}`);
                                return url ? (
                                  <a
                                    key={`${item.id}-lampiran-${fileIndex}`}
                                    className="btn btn-outline-primary btn-sm"
                                    href={url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {label}
                                  </a>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" style={{ zIndex: 1390 }} />
        </>
      )}
    </>
  );
}
